import { postEntitiesByFpIdVaultDecrypt } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { getLogger } from '@onefootprint/logger';
import type { DataIdentifier, Document, DocumentKind, Entity } from '@onefootprint/request-types/dashboard';
import { useEffect } from 'react';
import { create } from 'zustand';
import type { VaultType } from '@/entities/hooks/use-entity-vault';
import useEntityId from './use-entity-id';

const { logError } = getLogger({ location: 'use-decrypt-controls' });

type DecryptStep = 'idle' | 'selecting-fields' | 'selecting-reason';

type DecryptRequest = { entityId: string; fields: DataIdentifier[] };

type DecryptControlsStoreState = {
  step: DecryptStep;
  fields: DataIdentifier[];
  // Only track decrypted fields for the current entity (cleared when entity changes)
  currentEntityId: string | null;
  decryptedFields: DataIdentifier[];
  decryptedDocumentKinds: `${DocumentKind}-${number}`[];
  // One entry per request, so one that finishes doesn't clear fields another is still decrypting.
  pendingRequests: DecryptRequest[];
};

const initialState: DecryptControlsStoreState = {
  step: 'idle',
  fields: [],
  currentEntityId: null,
  decryptedFields: [],
  decryptedDocumentKinds: [],
  pendingRequests: [],
};

const useDecryptControlsStore = create<DecryptControlsStoreState>(() => initialState);

const forgetDecryptedFields = (entityId: string, fields: DataIdentifier[]) => {
  useDecryptControlsStore.setState(state =>
    state.currentEntityId === entityId
      ? { decryptedFields: state.decryptedFields.filter(field => !fields.includes(field)) }
      : state,
  );
};

const useDecryptControls = () => {
  const store = useDecryptControlsStore();
  const set = useDecryptControlsStore.setState;
  const get = useDecryptControlsStore.getState;
  const showErrorToast = useRequestErrorToast();
  const entityId = useEntityId();

  // Clear decrypted fields when entity changes to ensure the store doesn't contain a previous entity's fields when auto-decryption is triggered.
  // This is also reset in DecryptForm on mount, but useEntityInitialData (where auto-decryption is triggered) may run before DecryptForm resets.
  useEffect(() => {
    if (entityId && store.currentEntityId !== entityId) {
      set({
        currentEntityId: entityId,
        decryptedFields: [],
        decryptedDocumentKinds: [],
      });
    }
  }, [entityId, store.currentEntityId, set]);

  const decryptedFields = store.currentEntityId === entityId ? store.decryptedFields : [];
  const decryptedDocumentKinds = store.currentEntityId === entityId ? store.decryptedDocumentKinds : [];
  const fieldsBeingDecrypted = store.pendingRequests
    .filter(request => request.entityId === entityId)
    .flatMap(request => request.fields);
  const isDecrypting = (fields: DataIdentifier[]) => fields.some(field => fieldsBeingDecrypted.includes(field));

  const defer = () => {
    return new Promise(resolve => setTimeout(resolve, 0));
  };

  const startToSelectFields = () => {
    set({ step: 'selecting-fields' });
  };

  const selectAllFields = async () => {
    const documentKinds = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="documentKinds"]'));
    const fields = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="fields"]'));
    fields.forEach(checkbox => {
      checkbox.click();
    });
    for (const checkbox of documentKinds) {
      checkbox.click();
      await defer();
    }
  };

  const startToDecryptAll = async () => {
    await selectAllFields();
    selectReason();
  };

  const selectReason = async () => {
    set({ step: 'selecting-reason' });
  };

  const cancel = () => {
    set({ step: 'idle' });
  };

  const reset = () => {
    // The form can mount after auto-decrypt starts; only request completion clears its loading state.
    set(state => ({ ...initialState, pendingRequests: state.pendingRequests }));
  };

  const getDisFromDocuments = (documents: Document[], vaultData: VaultType) => {
    const imageDis = documents.flatMap(d =>
      d.uploads.map(upload => `${upload.identifier}:${upload.version}` as DataIdentifier),
    );

    const completedVersionDis = documents.flatMap(
      ({ completedVersion, kind, sambaActivityHistoryCompletedVersion }) => {
        if (!completedVersion) {
          return [];
        }

        const documentDataKinds = Object.keys(vaultData.dataKinds).filter(key => key.startsWith(`document.${kind}`));
        return documentDataKinds.map(di => {
          const isSambaActivityDI = di.includes('samba_activity_history_response');
          const useSambaVersion = isSambaActivityDI && sambaActivityHistoryCompletedVersion;
          const version = useSambaVersion ? sambaActivityHistoryCompletedVersion : completedVersion;
          return `${di}:${version}` as DataIdentifier;
        });
      },
    );

    return [...new Set([...imageDis, ...completedVersionDis])];
  };

  const getDocumentKindsFromFields = (fields: DataIdentifier[]) => {
    const documentKinds = fields
      .filter(field => field.startsWith('document.'))
      .map(field => {
        const parts = field.split('.');
        const kind = parts[1];
        const version = field.split(':')[1];
        return `${kind}-${version}` as `${DocumentKind}-${number}`;
      });
    return [...new Set(documentKinds)];
  };

  const decrypt = async ({ entity, fields, reason }: { entity: Entity; fields: DataIdentifier[]; reason: string }) => {
    const entityId = entity.id;
    const state = get();
    // Only use decrypted fields if they're for the current entity
    const currentDecryptedFields = state.currentEntityId === entityId ? state.decryptedFields : [];
    const currentDecryptedDocumentKinds = state.currentEntityId === entityId ? state.decryptedDocumentKinds : [];

    const fieldsToDecrypt = fields.filter(field => !currentDecryptedFields.includes(field));

    // The edit dialog reads us_tax_id, which needs its own decryption and permission even when ssn9/itin is allowed.
    const canDecryptUsTaxId = entity.data.some(attr => attr.identifier === 'id.us_tax_id' && attr.isDecryptable);
    const hasSsnOrItin = fieldsToDecrypt.some(field => field === 'id.ssn9' || field === 'id.itin');
    if (canDecryptUsTaxId && hasSsnOrItin && !fieldsToDecrypt.includes('id.us_tax_id')) {
      fieldsToDecrypt.push('id.us_tax_id');
    }

    const request: DecryptRequest = { entityId, fields: fieldsToDecrypt };
    set(state => ({ pendingRequests: [...state.pendingRequests, request] }));
    try {
      const documentKindsToDecrypt = getDocumentKindsFromFields(fieldsToDecrypt).filter(
        kind => !currentDecryptedDocumentKinds.includes(kind),
      );
      const response = await postEntitiesByFpIdVaultDecrypt({
        path: {
          fpId: entityId,
        },
        body: {
          fields: fieldsToDecrypt,
          reason,
        },
      });
      set(state => {
        if (state.currentEntityId !== entityId) {
          return state;
        }
        return {
          step: 'idle',
          decryptedFields: [...state.decryptedFields, ...fieldsToDecrypt],
          decryptedDocumentKinds: [...state.decryptedDocumentKinds, ...documentKindsToDecrypt],
        };
      });
      return response.data;
    } catch (error) {
      logError('Vault decryption error', error, {
        entityId,
        fields: fieldsToDecrypt.join(','),
      });

      set(state => (state.currentEntityId === entityId ? { step: 'selecting-reason' } : state));
      showErrorToast(error);
      return null;
    } finally {
      // A task later, once the caller has written the response to the vault. Cleared sooner, a field reads as
      // encrypted for a frame between its spinner and its value.
      setTimeout(() => {
        set(state => ({ pendingRequests: state.pendingRequests.filter(pending => pending !== request) }));
      });
    }
  };

  return {
    ...store,
    decryptedFields,
    decryptedDocumentKinds,
    isDecrypting,
    startToSelectFields,
    selectAllFields,
    startToDecryptAll,
    selectReason,
    cancel,
    reset,
    getDisFromDocuments,
    decrypt,
  };
};

export { forgetDecryptedFields };
export default useDecryptControls;
